import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  toastSuccess,
  FormCol,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';

import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal } from 'shared-pages/components';
import {
  FormInput,
  gqlAdd,
  gqlDelete,
  gqlGetByIdQuery,
  gqlUpdate,
  ProductionLineInput,
} from '../graphql';
import { FormFields } from './production-form-fields';
import { ProductionLine } from './productionExtraTable';
import { convertFieldToInt } from 'shared-pages/common/form-helpfunc';

export const ProductionFormPage = ({ isModal = false, paramId = undefined, modalOnClose }) => {
  const { handleGraphqlError } = useHandleError('production');
  const { appStore } = useRootStore();
  const history = useHistory();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = Number(id) || -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: Yup.number().integer(),
    code: YupString,
    seriesId: YupNumeric,
    codeNumber: YupNumeric,
    codePrefix: YupString,
    productionDate: Yup.date().required(t('Η ημερομηνία παραγωγής είναι υποχρεωτική')),
    notes: Yup.string().nullable(),
    isActive: Yup.boolean(),
    collectorId: Yup.number().integer().nullable(),
    fromWastereceptionpointId: Yup.number().integer().nullable(),
    fromCollectionId: Yup.number().integer().nullable(),
    fromCollection: Yup.object()
      .shape({
        id: Yup.number().integer(),
        code: Yup.string(),
      })
      .nullable(),
    productionLines: Yup.array()
      .of(
        Yup.object().shape({
          id: YupNumeric.nullable(), // Allow null for optional fields
          aa: YupNumeric.nullable(),
          materialId: YupNumeric,
          netWeight: YupNumeric.min(0),
          productionId: YupNumeric,
          producedMaterials: Yup.array().of(
            Yup.object().shape({
              id: YupNumeric.nullable(),
              materialId: YupNumeric,
              productionLineId: YupNumeric,
              material: Yup.object().shape({
                id: YupNumeric,
                name: YupString,
              }),
              netWeight: YupNumeric.min(0),
              percentage: YupNumeric.min(0),
            }),
          ),

          material: Yup.object().shape({
            id: YupNumeric,
            name: YupString,
          }),
        }),
      )
      .min(1, t('Απαιτείται τουλάχιστον μία γραμμή παραγωγής')),
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const {
    data: rawData,
    error,
    refetch,
  } = useQuery(gqlGetByIdQuery, {
    variables: { id: idNum },
    skip: isNew,
    fetchPolicy: 'network-only', // 👈 This forces a fresh request
  });
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<
    | { collectorId: Number; isActive: boolean; productionLines: any[]; productionDate: Date }
    | undefined
  >(undefined);

  useEffect(() => {
    if (isNew) {
      setInitialNewRecData({
        isActive: true,
        productionDate: new Date(),
        productionLines: [] as ProductionLine[],
        collectorId: appStore.isCollector ? appStore.associatedId : null,
      });
    }
  }, []);
  const handleSubmit = async (values, actions) => {
    console.log('handleSubmit', values, actions);
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
    convertFieldToInt(mutValues, ['seriesId', 'codeNumber']);
    mutValues.productionLines = values.productionLines.map((line) => {
      const newLine = Lo.pick(line, Lo.keys(new ProductionLineInput())); // If you have that input schema
      delete newLine.material;
      delete newLine.__typename;

      newLine.netWeight = parseFloat(line.netWeight);

      if (line.producedMaterials) {
        newLine.producedMaterials = line.producedMaterials.map((pm) => {
          const cleaned = {
            ...pm,

            materialId: pm.materialId,
            netWeight: parseFloat(pm.netWeight),
            percentage: parseFloat(pm.percentage),
          };
          delete cleaned.__typename;
          delete cleaned.material;
          return cleaned;
        });
      }

      return newLine;
    });

    if (isNew) {
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Η εγγραφή παραγωγής προστέθηκε με επιτυχία [${rec?.id}]`);
          history.push(`/production/${rec.id}`);
          return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
      if (rec && isModal && modalOnClose) {
        modalOnClose(rec.id);
      }
    } else {
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(
            `Η εγγραφή παραγωγής ενημερώθηκε με επιτυχία [${d.data[Object.keys(d.data)[0]].id}]`,
          );
          //reload with history
          history.push(`/production/${idNum}`);
        })
        .catch((error) => handleGraphqlError('mutation-update', error));
    }
  };

  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: t('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την εγγραφή;'),
    });

    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
  }, [deleteRecord, idNum]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        title={isNew ? t('Νέα Παραγωγή') : `${t('Παραγωγή')}: ${data?.code}`}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onReturn={null}
        permitSubmit={false}
        permitDelete={false}
        initialValues={isNew ? initialNewRecData : pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
        injectFooterElements={
          !isModal
            ? undefined
            : (formikCtx) => {
                return (
                  <ButtonCloseModal
                    onClick={() => {
                      modalOnClose(-1);
                    }}
                  />
                );
              }
        }
      >
        <FormFields
          id={idNum}
          isNew={isNew}
          updateRecord={updateRecord}
          refetch={refetch}
          data={data}
        />

        {isModal && <ButtonCloseModal onClick={() => modalOnClose(-1)} />}
      </FormCard>
    );
  }
};
